import axios from 'axios';
import Env from 'Services/Env';
import Store from 'Services/Store';
import CookieStorage from 'Core/commons/CookieStorage';

const QUERY_TIMEOUT = 120000;
class WriterService {
    constructor() {
        if (!WriterService.instance) {
            this.baseUrl = `${Env.get('sellsyAi')}/writer`;

            WriterService.instance = this;
        }

        return WriterService.instance;
    }

    async fetchWithToken(...args) {
        let [resource, config] = args;

        args.signal?.addEventListener('abort', function () {
            return Promise.reject(new DOMException('Aborted', 'AbortError'));
        });

        const writerService = new WriterService();

        const token = await writerService.getAuthorizationToken();
        config = {
            ...config,
            timeout: QUERY_TIMEOUT,
            responseType: 'stream',
            headers: {
                ...config.headers,
                'Content-Type': 'application/json; charset=UTF-8',
                Authorization: token,
            },
        };

        const response = await fetch(resource, config);

        return new Promise((resolve, reject) => {
            if (response.status === 200) {
                window.sellsyapp.config.globalProperties.$store.dispatch('corp/fetchQuotaAI');
                resolve(response);
            }

            reject(new Error(response.statusText));
        });
    }

    async adapt({ text, type, source, task, signal = undefined, retry = false }) {
        return await this.fetchWithToken(`${this.baseUrl}/adapt`, {
            method: 'POST',
            signal,
            body: JSON.stringify({ text, type, source, task, retry }),
        });
    }

    async translate({ text, type, source, language, signal = undefined, retry = false }) {
        return await this.fetchWithToken(`${this.baseUrl}/translate`, {
            method: 'POST',
            signal,
            body: JSON.stringify({ text, type, source, language, retry }),
        });
    }

    async changeTone({ text, type, source, tone, signal = undefined, retry = false }) {
        return await this.fetchWithToken(`${this.baseUrl}/change-tone`, {
            method: 'POST',
            signal,
            body: JSON.stringify({ text, type, source, tone, retry }),
        });
    }

    async write({ type, source, task, language, signal = undefined, retry = false }) {
        return await this.fetchWithToken(`${this.baseUrl}/write`, {
            method: 'POST',
            signal,
            body: JSON.stringify({ type, source, task, language, retry }),
        });
    }

    async generate({ text, type, source, signal = undefined, retry = false }) {
        return await this.fetchWithToken(`${this.baseUrl}/generate`, {
            method: 'POST',
            signal,
            body: JSON.stringify({ text, type, source, retry }),
        });
    }

    async generateTag({
        text,
        type,
        source = 'email-template',
        signal = undefined,
        retry = false,
    }) {
        return await this.fetchWithToken(`${this.baseUrl}/generate-tag`, {
            method: 'POST',
            signal,
            body: JSON.stringify({ text, type, source, retry }),
        });
    }

    /**
     * Get authorization token
     * @returns {Promise<unknown>|Promise<string>}
     */
    async getAuthorizationToken() {
        const currentStaffId = Store.get('user').id;
        const cookieStorage = new CookieStorage('api', 'access_token');
        //Get token in localStorage
        const cached = cookieStorage.get();
        if (cached) {
            const expire = new Date(cached.expireAt).getTime() - 60000; //-60s
            const now = new Date().getTime();
            if (cached.staffId === currentStaffId && expire > now) {
                return Promise.resolve('Bearer ' + cached.token);
            }
        }

        //Get token from saas
        const client = axios.create({
            timeout: QUERY_TIMEOUT,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        return new Promise(function (resolve, reject) {
            client.post('/web-token').then(function (response) {
                if (response.status !== 201 || !response.data.token) {
                    return reject(
                        new Error(
                            window.i18n.gettext("Une erreur d'authentification s'est produite"),
                        ),
                    );
                }

                const cachedData = response.data;
                cachedData.staffId = currentStaffId;
                cookieStorage.set(cachedData);

                return resolve('Bearer ' + response.data.token);
            });
        });
    }
}

export default WriterService;
